import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["form", "submit", "error"]
  static values = {
    submitting: { type: Boolean, default: false }
  }

  connect() {
    this.setupEntityFields()
    this.setupCollectionFields()
    this.setupFilePreviews()
    this.setupValidation()
  }

  submit(event) {
    // Prevent double submission
    if (this.submittingValue) {
      event.preventDefault()
      return
    }

    // Validate form before submission
    if (!this.validateForm()) {
      event.preventDefault()
      this.showFormError('Please fill in all required fields.')
      return
    }

    this.submittingValue = true
    
    // Update submit button if target exists
    if (this.hasSubmitTarget) {
      this.submitTarget.disabled = true
      this.submitTarget.dataset.originalText = this.submitTarget.textContent
      this.submitTarget.textContent = "Submitting..."
    }
    
    // Add loading state to form
    if (this.hasFormTarget) {
      this.formTarget.classList.add('opacity-75', 'pointer-events-none')
    } else {
      this.element.classList.add('opacity-75', 'pointer-events-none')
    }
  }

  // Reset form state (useful if submission fails via JS)
  reset() {
    this.submittingValue = false
    
    if (this.hasSubmitTarget) {
      this.submitTarget.disabled = false
      if (this.submitTarget.dataset.originalText) {
        this.submitTarget.textContent = this.submitTarget.dataset.originalText
      }
    }
    
    const form = this.hasFormTarget ? this.formTarget : this.element
    form.classList.remove('opacity-75', 'pointer-events-none')
  }

  setupEntityFields() {
    const entityFields = this.element.querySelectorAll('[data-entity-field="true"]')
    
    entityFields.forEach(field => {
      field.addEventListener('input', this.debounce((event) => {
        this.searchEntities(event.target)
      }, 300))
    })
  }

  setupCollectionFields() {
    const collectionFields = this.element.querySelectorAll('[data-collection-field="true"]')
    
    collectionFields.forEach(field => {
      // Initialize with available options
      this.loadCollectionOptions(field)
    })
  }

  setupFilePreviews() {
    const fileInputs = this.element.querySelectorAll('input[type="file"]')
    
    fileInputs.forEach(input => {
      input.addEventListener('change', (event) => {
        this.previewFile(event.target)
      })
    })
  }

  setupValidation() {
    const requiredFields = this.element.querySelectorAll('[required]')
    
    requiredFields.forEach(field => {
      field.addEventListener('blur', (event) => {
        this.validateField(event.target)
      })
      
      // Clear error on input
      field.addEventListener('input', (event) => {
        if (event.target.checkValidity()) {
          this.hideFieldError(event.target)
          event.target.classList.remove('border-red-500', 'ring-red-500', 'outline-red-500')
        }
      })
    })
  }

  async searchEntities(input) {
    const query = input.value.trim()
    if (query.length < 2) return

    try {
      const response = await fetch(`/v1/entities/search?q=${encodeURIComponent(query)}`)
      const entities = await response.json()
      
      this.showAutocomplete(input, entities)
    } catch (error) {
      console.error('Error searching entities:', error)
    }
  }

  async loadCollectionOptions(select) {
    const collection = select.dataset.collection
    if (!collection) return

    try {
      const response = await fetch(`/v1/collections/${collection}/entries`)
      const entries = await response.json()
      
      select.innerHTML = '<option value="">Select an option</option>'
      entries.forEach(entry => {
        const option = document.createElement('option')
        option.value = entry.id
        option.textContent = entry.title || entry.handle
        select.appendChild(option)
      })
    } catch (error) {
      console.error('Error loading collection options:', error)
    }
  }

  previewFile(input) {
    if (input.files && input.files[0]) {
      const file = input.files[0]
      
      if (file.type.startsWith('image/')) {
        const reader = new FileReader()
        reader.onload = (e) => {
          this.showImagePreview(input, e.target.result)
        }
        reader.readAsDataURL(file)
      }
    }
  }

  showImagePreview(input, src) {
    // Remove existing preview if present
    const existingPreview = input.parentNode.querySelector('.image-preview')
    if (existingPreview) {
      existingPreview.remove()
    }

    const preview = document.createElement('div')
    preview.className = 'image-preview mt-2'
    preview.innerHTML = `
      <img src="${src}" alt="Preview" class="h-20 w-20 object-cover rounded-md border border-gray-300">
      <p class="text-xs text-gray-500 mt-1">Image preview</p>
    `
    
    input.parentNode.appendChild(preview)
  }

  showAutocomplete(input, entities) {
    // Remove existing autocomplete
    const existing = input.parentNode.querySelector('.autocomplete-dropdown')
    if (existing) existing.remove()

    if (entities.length === 0) return

    const dropdown = document.createElement('div')
    dropdown.className = 'autocomplete-dropdown absolute z-10 w-full bg-white border border-gray-300 rounded-md shadow-lg mt-1'
    
    entities.forEach(entity => {
      const item = document.createElement('div')
      item.className = 'px-3 py-2 hover:bg-gray-100 cursor-pointer'
      item.textContent = entity.title || entity.handle
      item.addEventListener('click', () => {
        input.value = entity.id
        dropdown.remove()
      })
      dropdown.appendChild(item)
    })

    // Position the dropdown
    input.parentNode.style.position = 'relative'
    input.parentNode.appendChild(dropdown)

    // Close on click outside
    document.addEventListener('click', function closeDropdown(e) {
      if (!input.parentNode.contains(e.target)) {
        dropdown.remove()
        document.removeEventListener('click', closeDropdown)
      }
    })
  }

  validateField(field) {
    const isValid = field.checkValidity()
    
    if (!isValid) {
      field.classList.add('border-red-500')
      this.showFieldError(field, field.validationMessage)
    } else {
      field.classList.remove('border-red-500')
      this.hideFieldError(field)
    }
    
    return isValid
  }

  validateForm() {
    const requiredFields = this.element.querySelectorAll('[required]')
    let isValid = true
    let firstInvalid = null
    
    requiredFields.forEach(field => {
      if (!this.validateField(field)) {
        isValid = false
        if (!firstInvalid) firstInvalid = field
      }
    })
    
    // Focus first invalid field
    if (firstInvalid) {
      firstInvalid.focus()
      firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
    
    return isValid
  }

  showFieldError(field, message) {
    this.hideFieldError(field)
    
    const error = document.createElement('p')
    error.className = 'field-error text-sm text-red-600 mt-1'
    error.setAttribute('role', 'alert')
    error.textContent = message
    
    // Check if we're in a flex row container - if so, we need special handling
    const wrapper = field.closest('div')
    if (wrapper) {
      const computedStyle = window.getComputedStyle(wrapper)
      const isFlexRow = computedStyle.display === 'flex' && 
                        (computedStyle.flexDirection === 'row' || computedStyle.flexDirection === '')
      
      if (isFlexRow) {
        // Add flex-wrap to the container and force error to take full width on new line
        wrapper.classList.add('flex-wrap')
        error.classList.add('w-full', 'basis-full')
        // Insert after the field
        field.insertAdjacentElement('afterend', error)
      } else {
        // For non-flex-row containers, just append to wrapper
        wrapper.appendChild(error)
      }
    } else {
      field.insertAdjacentElement('afterend', error)
    }
  }

  hideFieldError(field) {
    // Look for the error element that follows this field
    const nextSibling = field.nextElementSibling
    if (nextSibling && nextSibling.classList.contains('field-error')) {
      nextSibling.remove()
      return
    }
    
    // Fallback: look in the wrapper
    const wrapper = field.closest('div')
    const container = wrapper || field.parentNode
    const existing = container.querySelector('.field-error')
    if (existing) existing.remove()
  }

  showFormError(message) {
    // Remove existing form error
    const existing = this.element.querySelector('.form-error')
    if (existing) existing.remove()

    const error = document.createElement('div')
    error.className = 'form-error bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md mb-4'
    error.setAttribute('role', 'alert')
    error.textContent = message
    
    // Insert at the beginning of the form
    const form = this.hasFormTarget ? this.formTarget : this.element
    const firstChild = form.querySelector('input[type="hidden"]')
    if (firstChild && firstChild.nextSibling) {
      form.insertBefore(error, firstChild.nextSibling)
    } else {
      form.insertBefore(error, form.firstChild)
    }
    
    // Scroll to error
    error.scrollIntoView({ behavior: 'smooth', block: 'center' })
    
    // Auto-remove after 5 seconds
    setTimeout(() => error.remove(), 5000)
  }

  // Dismiss error messages
  dismissError(event) {
    event.target.closest('.form-error, .field-error')?.remove()
  }

  debounce(func, wait) {
    let timeout
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout)
        func(...args)
      }
      clearTimeout(timeout)
      timeout = setTimeout(later, wait)
    }
  }
}
